import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styles from "./admin.module.scss";
import Button from "./Button";
import cx from "./cx";
import { TextField } from "./fields";
import Icon from "./Icon";

export interface ConfirmDialogProps {
  open: boolean;
  title: React.ReactNode;
  children?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** "danger" gives a red confirm button and warning icon. */
  tone?: "default" | "danger";
  busy?: boolean;
  confirmDisabled?: boolean;
  /** The user must type this exact text before confirming. */
  requireText?: string;
  requireTextLabel?: React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

const focusable =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  children,
  confirmLabel = "Bekreft",
  cancelLabel = "Avbryt",
  tone = "default",
  busy,
  confirmDisabled,
  requireText,
  requireTextLabel,
  onConfirm,
  onCancel,
}) => {
  const titleId = useId();
  const modalRef = useRef<HTMLDivElement>(null);
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!open) return;
    setTyped("");
    const previous = document.activeElement as HTMLElement | null;
    const first = modalRef.current?.querySelector<HTMLElement>(
      requireText ? "input" : `[data-autofocus]`,
    );
    first?.focus();
    return () => previous?.focus?.();
  }, [open]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && !busy) {
      e.stopPropagation();
      onCancel();
    }
    if (e.key === "Tab" && modalRef.current) {
      const items = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(focusable),
      );
      if (items.length === 0) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
  };

  const textOk =
    requireText === undefined || typed.trim() === requireText.trim();

  return createPortal(
    <div
      className={styles.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        ref={modalRef}
        className={styles.modal}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
      >
        <h2 className={styles.mh} id={titleId}>
          <span
            className={cx(
              styles.mhIcon,
              tone === "danger" && styles.mhIconDanger,
            )}
            aria-hidden="true"
          >
            <Icon name={tone === "danger" ? "trash" : "info"} size={18} />
          </span>
          {title}
        </h2>
        {(children || requireText !== undefined) && (
          <div className={styles.mb}>
            {children}
            {requireText !== undefined && (
              <TextField
                label={
                  requireTextLabel ?? (
                    <>
                      Skriv <b>{requireText}</b> for å bekrefte
                    </>
                  )
                }
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
              />
            )}
          </div>
        )}
        <div className={styles.mf}>
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={busy}
            data-autofocus={tone === "danger" ? true : undefined}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "danger" ? "dangerSolid" : "primary"}
            onClick={onConfirm}
            loading={busy}
            disabled={confirmDisabled || !textOk}
            data-autofocus={tone === "danger" ? undefined : true}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export type ConfirmOptions = Omit<
  ConfirmDialogProps,
  "open" | "onConfirm" | "onCancel" | "busy" | "children"
> & { message?: React.ReactNode };

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Gives `useConfirm()` – a promise based replacement for window.confirm. */
export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<
    (ConfirmOptions & { resolve: (ok: boolean) => void }) | null
  >(null);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => setState({ ...options, resolve })),
    [],
  );

  const close = (ok: boolean) => {
    state?.resolve(ok);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <ConfirmDialog
          {...state}
          open
          onConfirm={() => close(true)}
          onCancel={() => close(false)}
        >
          {state.message}
        </ConfirmDialog>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
};
